import { memo, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { useGetAttendanceRecordsLazyQuery } from "src/generated/graphql";
import { Box, Text, Flex, Loader, useMantineTheme } from "@mantine/core";
import { BsCalendar, BsClockHistory } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { BiBlock, BiCheck, BiSolidDiscount } from "react-icons/bi";
import { TbMapPin } from "react-icons/tb";
import { formatDate, getTimeInAMPMFormat } from "src/utils/dateHelper";
import { ClientCardBg } from "src/utils/constants";

const getAttendanceIcon = {
  Upcoming: { icon: <BsClockHistory color="#63B8E8" size={23} /> },
  Attended: { icon: <BiCheck color="#28C638" size={23} /> },
  Missed: { icon: <IoMdClose color="#4D4D4D" size={23} /> },
  Cancelled: {
    icon: <BiBlock color="#63B8E8" size={23} />,
  },
};

function Attendance() {
  const { currentClient } = useContext(AppContext);
  const theme = useMantineTheme();
  const [getAttendance, { data, loading }] = useGetAttendanceRecordsLazyQuery();

  useEffect(() => {
    currentClient?.id &&
      getAttendance({ variables: { clientId: currentClient.id } });
  }, [currentClient]);

  if (loading)
    return <Loader width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />;
  return (
    <>
      {data && data.attendanceByClient.length > 0 ? (
        [...data.attendanceByClient]
          .sort((a, b) => {
            return (
              new Date(b.meeting.startAt).valueOf() -
              new Date(a.meeting.startAt).valueOf()
            );
          })
          .map((e) => (
            <Box
              key={e.createdDate}
              mb={"md"}
              sx={{
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
                borderRadius: 12,
              }}
            >
              <Box
                sx={{ padding: "6px 12px", borderBottom: "1px solid #D9D9D9" }}
              >
                <Flex justify={"space-between"} align={"center"}>
                  <Text fw={600}>{e.meeting.name}</Text>
                  <Flex
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 40,
                      background: "white",
                    }}
                    align={"center"}
                    justify={"center"}
                  >
                    {getAttendanceIcon[e.attendanceStatus].icon}
                  </Flex>
                </Flex>
              </Box>

              <Flex
                justify={"space-between"}
                sx={{ padding: "10px 16px" }}
                wrap={"wrap"}
              >
                <Flex align={"center"} gap={4}>
                  <TbMapPin size={12} />
                  <Text size={13}>{e.meeting.location.name}</Text>
                </Flex>

                <Flex align={"center"} gap={4}>
                  <BsCalendar size={12} />
                  <Text size={13}>
                    {e.attendanceStatus}{" "}
                    {formatDate(e.meeting.startAt).formatshortYear}{" "}
                    {getTimeInAMPMFormat(e.meeting.startAt)}
                  </Text>
                </Flex>

                {e.creditAllotment && (
                  <Flex align={"center"} gap={4} sx={{ width: "100%" }}>
                    <BiSolidDiscount
                      size={12}
                      style={{ transform: "rotate(90deg)" }}
                    />
                    <Text size={13}>
                      Used {e.creditAllotment.count}{" "}
                      {e.creditAllotment.serviceAllotment?.name} -{" "}
                      {e.creditAllotment.serviceAllotment?.credits}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Box>
          ))
      ) : (
        <Text align="center" fw={"bolder"} size={"xl"}>
          No Attendance found
        </Text>
      )}
    </>
  );
}

export default memo(Attendance);
